import React, { useState } from "react";
import { X, Star, ShoppingBag, Heart, Truck, RefreshCcw, ShieldCheck, Minus, Plus } from "lucide-react";

export const QuickViewModal = ({ product, onClose, onAdd, onWish, wished }) => {
  const [qty, setQty] = useState(1);
  if (!product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl grid md:grid-cols-2">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center z-10 shadow"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="bg-[color:var(--brand-cream)] aspect-square md:aspect-auto md:min-h-[500px]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        </div>

        <div className="p-6 md:p-8 flex flex-col">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">{product.category}</div>
          <h3 className="mt-1 font-serif text-2xl md:text-3xl leading-tight">{product.name}</h3>

          <div className="mt-3 flex items-center gap-2 text-sm text-neutral-700">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`w-4 h-4 ${n <= Math.round(product.rating) ? "fill-[color:var(--brand-red)] text-[color:var(--brand-red)]" : "text-neutral-300"}`} />
              ))}
            </div>
            <span className="font-medium">{product.rating}</span>
            <span className="text-neutral-500">({product.reviews} reviews)</span>
          </div>

          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-[color:var(--brand-ink)]">${product.price.toFixed(2)}</span>
            {product.oldPrice && (
              <>
                <span className="text-base text-neutral-500 line-through">${product.oldPrice.toFixed(2)}</span>
                <span className="text-xs uppercase tracking-widest text-[color:var(--brand-red)] font-semibold">
                  Save ${(product.oldPrice - product.price).toFixed(2)}
                </span>
              </>
            )}
          </div>

          <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
            Beautifully crafted with premium materials, this piece blends timeless design with
            everyday comfort. Perfect for refreshing any space with a touch of warmth and character.
          </p>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-neutral-300 rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-neutral-100">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-10 text-center text-sm font-medium">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-neutral-100">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => { onAdd(product, qty); onClose(); }}
              className="flex-1 bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red-dark)] text-white py-3 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" /> Add to Bag
            </button>
            <button
              onClick={() => onWish(product)}
              className="w-12 h-12 border border-neutral-300 flex items-center justify-center hover:border-[color:var(--brand-red)] transition-colors"
              aria-label="Wishlist"
            >
              <Heart className={`w-5 h-5 ${wished ? "fill-[color:var(--brand-red)] text-[color:var(--brand-red)]" : "text-neutral-700"}`} />
            </button>
          </div>

          <div className="mt-6 border-t pt-4 space-y-2 text-xs text-neutral-600">
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[color:var(--brand-red)]" /> Free shipping on orders over $99</div>
            <div className="flex items-center gap-2"><RefreshCcw className="w-4 h-4 text-[color:var(--brand-red)]" /> 90-day easy returns</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[color:var(--brand-red)]" /> Lifetime craftsmanship guarantee</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SimpleModal = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white max-w-md w-full rounded-sm shadow-2xl p-6 md:p-8">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
        <div className="font-serif text-2xl mb-2">{title}</div>
        {children}
      </div>
    </div>
  );
};
